import posthog from 'posthog-js';
import { emit, on } from '../state/bus.js';
import { currentUser } from './auth.js';

/* PRODUCT ANALYTICS — PostHog Cloud.

   Same "additive, never required" shape as this app's other optional
   services (Supabase, Paddle): absent entirely, with every export
   below a safe no-op, whenever VITE_POSTHOG_KEY isn't set — a local/
   self-hosted build never talks to PostHog at all.

   Autocapture is off on purpose. This app is a canvas-heavy 3D tool where
   most real interaction is pointer drags on <canvas>, which autocapture
   can't see anyway — left on, it would only capture clicks on the
   surrounding chrome (topbar buttons, rail fields), which is mostly noise
   and burns the free tier's monthly event volume for little insight.
   track() below fires a small, deliberate set of product events instead —
   the ones that actually answer "are people getting value out of this":
   an export, a template applied, a credit pack purchased. */
const KEY = import.meta.env.VITE_POSTHOG_KEY;
const HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
/* The default above is PostHog's US region, and a project on the EU cloud
   (which this one is) will accept nothing sent there — events are simply
   dropped, with no error in the console and no gap that looks like a
   failure. That makes a forgotten VITE_POSTHOG_HOST in a deploy environment
   invisible until someone wonders why the dashboard is empty weeks later.
   Say it out loud instead: a key without an explicit host is very likely a
   misconfiguration, not a deliberate choice of the US region. */
if (KEY && !import.meta.env.VITE_POSTHOG_HOST) {
  console.warn(
    '[illusio] VITE_POSTHOG_KEY is set but VITE_POSTHOG_HOST is not — defaulting to ' +
    'the US region. If the project is on EU cloud, set VITE_POSTHOG_HOST to ' +
    'https://eu.i.posthog.com or every event will be silently discarded.'
  );
}
/* navigator.webdriver is set by every CDP-driven browser (Playwright,
   Puppeteer, Selenium) unless something goes out of its way to hide it,
   which nothing here does — tools/verify.mjs runs a real Chrome/Edge
   against the dev server, which bundles this same real key, so without
   this check every verification run would report real events (and a new
   PostHog person) into the actual project. */
/* That check alone still leaves a real gap: a HUMAN running `npm run dev`
   and clicking around by hand is a genuine, non-webdriver browser, and
   .env.local bundles the same real VITE_POSTHOG_KEY the production build
   uses — so ordinary local development has been reporting real pageviews
   and product events into the live project the whole time. Excluding
   localhost by hostname closes that, independently of webdriver. Checked
   against the hostname the browser is ACTUALLY running on, not an env var
   like NODE_ENV — a production build previewed locally (`npm run preview`)
   is exactly the case this should also catch, and an env flag wouldn't. */
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1']);
export const isLocalHost = LOCAL_HOSTS.has(location.hostname);

/* Same argument one step out: a Cloudflare Pages PREVIEW deployment of a
   feature branch is not localhost, so the localhost rule above does not
   catch it — but it is still someone trying a half-built branch, and its
   clicks are not product usage. Left in, a week of testing a branch lands
   in the same funnels as real customers and quietly moves the numbers the
   analytics exist to answer.

   Matched on the host rather than an env var so it holds however the
   preview was built. Production is the custom domain (app.illusio3d.com);
   only the project's own *.pages.dev hostnames are treated as previews,
   and the bare production Pages URL would be too — which is correct, it is
   not the address anybody is sent to. */
export const isPreviewHost = /\.pages\.dev$/i.test(location.hostname);

/* Whether analytics COULD run here — a real key, a real browser, the real
   host. Separate from whether it MAY, which only the visitor decides. */
export const analyticsAvailable =
  Boolean(KEY) && !navigator.webdriver && !isLocalHost && !isPreviewHost;

/* CONSENT.

   Product analytics is not strictly necessary for anything anyone came here
   to do, so under ePrivacy it needs asking first — and asking first has to
   mean posthog.init never runs, not that it runs and then opts out. The
   difference is the whole point: opting out after init has already written a
   distinct_id to the device, which is the thing consent was about.

   The choice itself is remembered in localStorage, which needs no consent of
   its own: you cannot honour a refusal you did not record.

   Deliberately never re-asked. Nagging somebody who said no is how consent
   stops being freely given; ui/settings.js has the way back for anyone who
   changes their mind.

   'no' and null behave identically at runtime and are stored differently on
   purpose — null is "not asked yet" and shows the bar, 'no' is an answer. */
const CONSENT_KEY = 'illusio3d.analytics-consent';
let consent = (() => {
  try { const v = localStorage.getItem(CONSENT_KEY);
        return v === 'yes' || v === 'no' ? v : null; }
  catch (_) { return null; }
})();
let started = false;

/** 'yes' | 'no' | null — null meaning nobody has been asked. */
export const analyticsConsent = () => consent;
/* ?consent on the URL shows the bar anywhere, so it can be looked at without
   deploying to production — which was otherwise the only host it appeared on,
   since previews, localhost and automated browsers are all excluded from
   analytics itself. It shows the BAR and nothing else: answering still has to
   pass analyticsAvailable before anything starts, so a preview cannot begin
   sending events however it is answered. Read once, before the magic-link
   handler in services/auth.js rewrites the URL. */
const forceBar = (() => {
  try { return new URLSearchParams(location.search).has('consent'); }
  catch (_) { return false; }
})();

/** Whether there is a question worth putting on screen. */
export const analyticsNeedsChoice = () =>
  (analyticsAvailable || forceBar) && consent === null;

function start() {
  if (started || !analyticsAvailable || consent !== 'yes') return;
  posthog.init(KEY, {
    api_host: HOST,
    person_profiles: 'identified_only',
    autocapture: false,
  });
  started = true;
  /* auth:changed has almost certainly already fired by the time anyone
     answers, so the identity has to be re-applied here or the first session
     after consent would land on an anonymous PostHog person. */
  identifyUser(currentUser());
}

/** @param {'yes'|'no'} v */
export function setAnalyticsConsent(v) {
  if (v !== 'yes' && v !== 'no') return;
  consent = v;
  try { localStorage.setItem(CONSENT_KEY, v); } catch (_) {}
  if (v === 'yes') start();
  /* Nothing to tear down on 'no' — if it was never started there is nothing
     running, and consent cannot be withdrawn mid-session from a state where
     it was never given. Turning it off after 'yes' takes a reload, which
     ui/settings.js says. */
  emit('analytics:consent', v);
}

start();

/**
 * Ties the analytics identity to the Supabase auth user, so an anonymous
 * visitor's early events and their later upgraded-to-email events land on
 * the same PostHog person — mirrors auth.js's own "same user id" upgrade.
 * Call with the user from an 'auth:changed' payload (or null on sign-out).
 */
export function identifyUser(user) {
  if (!started) return;
  if (!user) { posthog.reset(); return; }
  posthog.identify(user.id, {
    is_anonymous: user.is_anonymous === true,
    ...(user.email ? { email: user.email } : {}),
  });
}
on('auth:changed', identifyUser);

/** A deliberate product event. No-ops quietly if PostHog isn't configured. */
export function track(event, props) {
  /* Not "available" — STARTED. Events raised before anyone answered are
     dropped rather than queued: a queue would replay them the moment consent
     arrived, which is collecting first and asking after. */
  if (!started) return;
  posthog.capture(event, props);
}
